import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/axios';
import useProjectStore from '../stores/projectStore';
import {
  Ga4ConnectPanel,
  GscConnectPanel,
  CroTrackerConnectPanel,
} from '../components/integrations/ConnectPanels';

const STEPS = ['Unternehmen', 'Skillset', 'Datenquellen', 'Aktivieren'];
const ROLE_LABEL = {
  tracking: 'Analytics', seo: 'SEO/Rankings', onpage: 'OnPage', technik: 'Technik',
  content: 'Content', eeat: 'E-E-A-T', reporting: 'Reporting',
};

export default function Onboarding() {
  const navigate = useNavigate();
  const fetchProjects = useProjectStore((s) => s.fetchProjects);
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({ name: '', domain: '', color: '#0C9488' });
  const [project, setProject] = useState(null);
  const [skillsets, setSkillsets] = useState([]);
  const [skillset, setSkillset] = useState('standard');
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/onboarding/skillsets', { skipProject: true })
      .then(({ data }) => setSkillsets(data.skillsets || []))
      .catch(() => {});
  }, []);

  const createProject = async () => {
    setError(null); setBusy(true);
    try {
      const { data } = await api.post('/projects', { name: form.name, domain: form.domain || null, color: form.color });
      setProject(data);
      await fetchProjects();
      setCurrentProject(data.id);
      setStep(1);
    } catch (e) {
      setError(e.response?.data?.message || 'Projekt konnte nicht angelegt werden.');
    } finally { setBusy(false); }
  };

  const activate = async () => {
    setError(null); setBusy(true);
    try {
      const { data } = await api.post('/onboarding/activate', { project_id: project.id, skillset }, { skipProject: true });
      setResult(data);
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.error || 'Aktivierung fehlgeschlagen.');
    } finally { setBusy(false); }
  };

  const current = skillsets.find((s) => s.key === skillset);

  return (
    <div className="p-4 lg:p-6 max-w-2xl mx-auto">
      <h1 className="text-[22px] font-semibold tracking-tight text-[#0D3B50] mb-1">Neuer Kunde</h1>
      <p className="text-[13.5px] text-gray-500 mb-5">In wenigen Schritten startklar — Projekt, Agenten, Automatik.</p>

      {/* Step-Indikator */}
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-2 flex-1">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 ${i <= step ? 'bg-[#0D3B50] text-white' : 'bg-gray-200 text-gray-500'}`}>{i + 1}</div>
            <span className={`text-[12px] ${i === step ? 'text-[#0D3B50] font-medium' : 'text-gray-400'}`}>{label}</span>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-gray-200" />}
          </div>
        ))}
      </div>

      {error && <div className="mb-4 text-[12.5px] text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</div>}

      <div className="bg-white rounded-xl border border-black/[.07] p-5">
        {/* Schritt 1: Unternehmen */}
        {step === 0 && (
          <div className="space-y-3">
            <Field label="Unternehmen / Projektname *">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Muster GmbH" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal" />
            </Field>
            <Field label="Domain">
              <input value={form.domain} onChange={(e) => setForm({ ...form, domain: e.target.value })} placeholder="muster.de" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-teal" />
            </Field>
            <Field label="Kundenfarbe (für Reports)">
              <input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-9 w-16 border border-gray-200 rounded" />
            </Field>
            <div className="flex justify-end pt-2">
              <button onClick={createProject} disabled={busy || !form.name.trim()} className="px-5 py-2 text-[13px] bg-[#0D3B50] text-white rounded-lg hover:opacity-90 disabled:opacity-50">{busy ? 'Lege an…' : 'Weiter'}</button>
            </div>
          </div>
        )}

        {/* Schritt 2: Skillset */}
        {step === 1 && (
          <div className="space-y-3">
            <div className="text-[12.5px] text-gray-500">Welcher Stack an Agenten soll für <strong>{project?.name}</strong> aktiviert werden?</div>
            <div className="space-y-2">
              {skillsets.map((s) => (
                <label key={s.key} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${skillset === s.key ? 'border-[#0D3B50] bg-[#0D3B50]/[.04]' : 'border-gray-200 hover:bg-gray-50'}`}>
                  <input type="radio" name="skillset" checked={skillset === s.key} onChange={() => setSkillset(s.key)} className="mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[13.5px] text-[#111f30]">{s.label}</div>
                    <div className="text-[12px] text-gray-500">{s.description}</div>
                    <div className="text-[11px] text-gray-400 mt-1">{s.roles.map((r) => ROLE_LABEL[r] || r).join(' · ')}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-between pt-2">
              <button onClick={() => setStep(0)} className="px-4 py-2 text-[13px] border border-gray-200 rounded-lg hover:bg-gray-50">Zurück</button>
              <button onClick={() => setStep(2)} className="px-5 py-2 text-[13px] bg-[#0D3B50] text-white rounded-lg hover:opacity-90">Weiter</button>
            </div>
          </div>
        )}

        {/* Schritt 3: Datenquellen — schrittweises Abfragen der Anbindungen */}
        {step === 2 && (
          <DatenquellenStep
            projectId={project?.id}
            busy={busy}
            onBack={() => setStep(1)}
            onActivate={activate}
          />
        )}

        {/* Schritt 4: Fertig */}
        {step === 3 && result && (
          <div className="space-y-3 text-center">
            <div className="text-[32px]">🎉</div>
            <div className="text-[15px] font-semibold text-[#2d7d6f]">{project?.name} ist startklar</div>
            <div className="text-[12.5px] text-gray-500">{result.created} Agenten aktiviert. Die Automatik läuft (wöchentliche Prüfung, monatlicher Report).</div>
            <ul className="text-left max-w-sm mx-auto bg-gray-50 rounded-lg p-3 space-y-1">
              {(result.agents || []).map((a) => (
                <li key={a.role} className="flex items-center justify-between text-[12px]">
                  <span className="text-gray-700">{a.display_name}</span>
                  <span className="text-gray-400">{a.status}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-center gap-2 pt-2">
              <button onClick={() => navigate('/agenten')} className="px-5 py-2 text-[13px] bg-[#0D3B50] text-white rounded-lg hover:opacity-90">Zum Kunden</button>
              <button onClick={() => { setStep(0); setForm({ name: '', domain: '', color: '#0C9488' }); setProject(null); setResult(null); }} className="px-4 py-2 text-[13px] border border-gray-200 rounded-lg hover:bg-gray-50">Weiterer Kunde</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[12px] font-medium text-gray-700 block mb-1">{label}</label>
      {children}
    </div>
  );
}

// ─── Schritt „Datenquellen": Anbindungen schrittweise abfragen ───────────────
// 3a Analytics (GA4 via SSO + optional OptimusAI-Tracker) → 3b Search Console
// (SEO inkl. XOVI) → 3c Google Ads (in Vorbereitung). Jede Verbindung ist
// optional; verbunden wird direkt inline über die bestehenden Connect-Panels.
const DS_STEPS = ['Analytics', 'Search Console', 'Google Ads'];

function DatenquellenStep({ projectId, busy, onBack, onActivate }) {
  const [sub, setSub] = useState(0);
  const isLast = sub === DS_STEPS.length - 1;

  return (
    <div className="space-y-4">
      <div>
        <div className="text-[13px] text-gray-700 font-medium">Datenquellen verbinden</div>
        <p className="text-[12px] text-gray-500 mt-0.5">
          Schritt für Schritt — jede Verbindung ist optional und lässt sich später unter „Anbindungen" nachholen.
        </p>
      </div>

      {/* Sub-Step-Indikator */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {DS_STEPS.map((label, i) => (
          <div key={label} className="flex items-center gap-1.5">
            <span
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${
                i === sub
                  ? 'bg-[#0D3B50] text-white'
                  : i < sub
                    ? 'bg-[#0D3B50]/10 text-[#0D3B50]'
                    : 'bg-gray-100 text-gray-400'
              }`}
            >
              {i + 1}. {label}
            </span>
            {i < DS_STEPS.length - 1 && <span className="text-gray-300">›</span>}
          </div>
        ))}
      </div>

      <div className="border border-gray-100 rounded-xl p-4 bg-gray-50/40">
        {/* 3a — Analytics */}
        {sub === 0 && (
          <div className="space-y-5">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[13px] font-semibold text-gray-800">Google Analytics 4</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">SSO</span>
              </div>
              <Ga4ConnectPanel integration={{ type: 'ga4', config: {} }} projectId={projectId} />
            </div>
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[13px] font-semibold text-gray-800">OptimusAI Tracker</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-light text-teal font-medium">zusätzlich</span>
              </div>
              <p className="text-[11.5px] text-gray-500 mb-2">
                Eigenes Tracking-Snippet — als Ergänzung oder Alternative zu GA4. Beides kann parallel laufen.
              </p>
              <CroTrackerConnectPanel projectId={projectId} />
            </div>
          </div>
        )}

        {/* 3b — Search Console (SEO inkl. XOVI) */}
        {sub === 1 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[13px] font-semibold text-gray-800">Google Search Console</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-600 font-medium">SEO + XOVI</span>
            </div>
            <p className="text-[11.5px] text-gray-500 mb-3">
              Liefert Queries, Impressionen & Positionen. XOVI (Suchvolumen & Ranking-Historie) ist im SEO-Modul bereits mit dabei.
            </p>
            <GscConnectPanel integration={{ type: 'gsc', config: {} }} projectId={projectId} />
          </div>
        )}

        {/* 3c — Google Ads (in Vorbereitung) */}
        {sub === 2 && (
          <div className="text-center py-6">
            <div className="text-[14px] font-semibold text-gray-800">Google Ads (SEA)</div>
            <span className="inline-block mt-1.5 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-semibold">
              DEMNÄCHST
            </span>
            <p className="text-[12px] text-gray-500 mt-3 max-w-sm mx-auto leading-relaxed">
              Kampagnen-, Keyword- und Quality-Score-Daten für bezahlte Kampagnen. Diese Anbindung ist
              in Vorbereitung — hier ist aktuell nichts zu tun.
            </p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-1">
        <button
          onClick={() => (sub === 0 ? onBack() : setSub((s) => s - 1))}
          className="px-4 py-2 text-[13px] border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          Zurück
        </button>
        {!isLast ? (
          <button
            onClick={() => setSub((s) => s + 1)}
            className="px-5 py-2 text-[13px] bg-[#0D3B50] text-white rounded-lg hover:opacity-90"
          >
            Weiter
          </button>
        ) : (
          <button
            onClick={onActivate}
            disabled={busy}
            className="px-5 py-2 text-[13px] bg-[#0D3B50] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Aktiviere…' : 'Projekt aktivieren'}
          </button>
        )}
      </div>
    </div>
  );
}
